<template>
  <div
    v-show="showSuggestions && !disabled"
    class="_overlay"
    @click="reset"
    @mousemove="navigatingWith = 'mouse'"
  >
    <m-card
      ref="card"
      class="m-context-menu"
      :style="style"
      no-padding
      list
      placement="bottomLeft"
    >
      <template
        v-for="(section, sectionIndex) in sections"
        :key="section.type"
      >
        <m-divider
          v-if="sectionIndex > 0"
          xs
        />
        <m-card-item
          class="_sub-heading"
          :clickable="false"
          light
        >
          {{ section.title }}
          <m-spinner
            v-if="loading && sectionIndex === 0"
            size="xxs"
          />
        </m-card-item>
        <m-card-item
          v-for="(item, index) in section.items"
          :key="`${item.type}_${item.uid}`"
          :ref="`item_${section.offset + index}`"
          :focused="itemIndex === section.offset + index"
          class="_item"
          no-hover
          :emoji="item.type === 'user' ? undefined : item.icon"
          @mouseover="handleMouseover(section.offset + index)"
          @click="handleClick(item, $event)"
        >
          <user-display
            v-if="item.type === 'user'"
            :user="item"
            small
          />
          <template v-else>
            {{ item.title === '' ? $t('general.untitled') : item.title }}
          </template>
        </m-card-item>
      </template>
      <m-card-item
        v-if="orderedItems.length === 0"
        :clickable="false"
        no-hover
        class="_empty"
      >
        <m-spinner
          v-if="loading"
          size="xxs"
        />
        <template v-else>
          {{ noItemsText }}
        </template>
      </m-card-item>
    </m-card>
  </div>
</template>

<script>
import ComponentList from '@/components/editor/ComponentList';
import UserDisplay from 'shared/components/UserDisplay.vue';
import useDebounce from 'shared/composables/debounce';
import useGoals from '@/composables/goal/goals';
import useLoggedInUserAccount from '@/composables/logged-in-user-account/logged-in-user-account';
import usePageVisits from '@/composables/page-visits';
import useRepo from '@/nebula/repo';
import useSpaces from '@/composables/space/spaces';
import useUsers from '@/composables/user/users';
import { ContextMenuExtension } from '@/components/editor/context-menu-extension';
import { displayName } from 'shared/composables/user';
import { dogma } from '@/api';
import { editorNodeType } from 'shared/constants.json';
import { findInArray } from 'shared/lib/array/array';
import { goalsByTitle } from '@/api/query/nebula/goal';
import { gridPage as gridPageConfig, space as spaceConfig } from 'shared/api/query/configs.json';
import { insertText } from '@/tiptap/commands/commands';
import { isEmpty, isNullOrUndefined } from 'shared/lib/object/object';
import { markRaw } from 'vue';

export default {
  name: 'MMentionMenu',
  props: {
    editor: {
      type: Object,
      default: () => ({}),
    },
    allowedContent: {
      type: Array,
      default: () => [],
    },
    customItems: {
      type: Array,
      default: () => [],
    },
  },
  setup() {
    const { debounce, cancel: debounceCancel } = useDebounce();
    const { goals, selectSingle } = useGoals();
    const { users, searchUsers } = useUsers();
    const { loggedInUserAccount } = useLoggedInUserAccount();
    const { getEnrichedPageVisits } = usePageVisits();
    const { spaces: activeSpaces } = useSpaces();
    const { entityList: gridPages } = useRepo(gridPageConfig.model);
    return {
      activeSpaces,
      gridPages,
      debounce,
      debounceCancel,
      goals,
      selectSingle,
      users,
      account: loggedInUserAccount,
      getEnrichedPageVisits,
      searchUsers,
    };
  },
  components: { UserDisplay },
  data() {
    return {
      navigatingWith: 'mouse',
      loading: false,
      query: null,
      schema: null,
      view: null,
      suggestionRange: null,
      filteredItems: [],
      itemIndex: 0,
      disabled: false,
      command: () => {
      },
      x: 0,
      y: 0,
      bottom: 0,
      recentlyVisitedItems: [],
    };
  },
  computed: {
    noItemsText() {
      if (this.allowedContent.includes(editorNodeType.goalMention) && this.allowedContent.includes(editorNodeType.mention)) {
        return this.$t('mMentionMenu.noItems', { title: this.account.goalSettings.featureNamePlural });
      }
      if (this.allowedContent.includes(editorNodeType.goalMention)) {
        return this.$t('mMentionMenu.noGoals', { title: this.account.goalSettings.featureNamePlural });
      }
      return this.$t('mMentionMenu.noUsers');
    },
    sections() {
      let offset = 0;
      return [
        { type: 'user', title: this.$t('mMentionMenu.users') },
        { type: 'goal', title: this.$t('mMentionMenu.goalsTitle', { title: this.account.goalSettings.featureNamePlural }), hidden: !this.account.accountSettings.usesGoals },
        { type: spaceConfig.model, title: this.$t('mMentionMenu.spaces') },
        { type: gridPageConfig.model, title: this.$t('mMentionMenu.dashboards') },
      ]
        .filter((section) => section.hidden !== true)
        .map((section) => ({
          ...section,
          items: this.filteredItems
            .filter((i) => i.type === section.type)
            .sort((a, b) => this.matchRank(a) - this.matchRank(b))
            .slice(0, this.sectionLimit),
        }))
        .filter((section) => section.items.length > 0)
        .sort((a, b) => this.matchRank(a.items[0]) - this.matchRank(b.items[0]))
        .map((section) => {
          const withOffset = { ...section, offset };
          offset += section.items.length;
          return withOffset;
        });
    },
    orderedItems() {
      return this.sections.flatMap((section) => section.items);
    },
    resourcesAllowed() {
      return this.allowedContent.includes(editorNodeType.resourceMention);
    },
    // with four sections, a broad goal search would push every team and dashboard out of view
    sectionLimit() {
      return this.resourcesAllowed ? 5 : Infinity;
    },
    style() {
      const width = 32;
      const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
      const left = Math.max(0, Math.min(this.x, window.innerWidth - width * rootFontSize - 8));
      const placement = this.bottom !== 0 ? { bottom: `${this.bottom}px` } : { top: `${this.y + 20}px` };
      return {
        width: `${width}rem`,
        position: 'fixed',
        left: `${left}px`,
        ...placement,
        zIndex: 1070,
        maxHeight: this.$store.state.breakpoint.smAndDown ? 'var(--viewport-height-100)' : '41.4rem',
        overflow: 'auto',
      };
    },
    initialGoals() {
      const recentlyVisitedGoals = this.recentlyVisitedItems
        .filter((i) => i.type === 'goal')
        .map((g) => {
          const storeGoal = this.selectSingle(g.uid);
          if (!isNullOrUndefined(storeGoal)) {
            return storeGoal;
          }
          return g;
        });
      return [
        ...recentlyVisitedGoals,
        ...this.goals.filter((g) => !recentlyVisitedGoals.map((item) => item.uid).includes(g.uid)),
      ];
    },
    items() {
      const res = [];
      if (this.allowedContent.includes(editorNodeType.mention)) {
        res.push(
          ...this.users.map((u) => ({
            ...u,
            type: 'user',
            title: displayName(u),
          })).slice(0, 3),
        );
      }
      if (this.allowedContent.includes(editorNodeType.goalMention)) {
        res.push(
          ...this.initialGoals.map((g) => ({
            ...g,
            type: 'goal',
          })).slice(0, 3),
        );
      }
      if (this.resourcesAllowed) {
        res.push(
          ...this.resources(this.activeSpaces, spaceConfig.model).slice(0, 3),
          ...this.resources(this.gridPages, gridPageConfig.model).slice(0, 3),
        );
      }
      return res;
    },
    showSuggestions() {
      return this.query !== null;
    },
  },
  methods: {
    // the goal search is not ranked, so a weak match can stand above an exact one in
    // another section, where Enter would pick it
    matchRank(item) {
      if (isEmpty(this.query)) {
        return 0;
      }
      const title = (item.title ?? '').toLowerCase();
      const query = this.query.toLowerCase();
      if (title === query) {
        return 0;
      }
      return title.startsWith(query) ? 1 : 2;
    },
    resources(entities, type) {
      return entities.map((e) => ({ uid: e.uid, title: e.title ?? '', icon: e.icon, type }));
    },
    matchingResources(query) {
      const matches = (r) => r.title.toLowerCase().includes(query.toLowerCase());
      return [
        ...this.resources(this.activeSpaces, spaceConfig.model).filter(matches),
        ...this.resources(this.gridPages, gridPageConfig.model).filter(matches),
      ];
    },
    doGetMentions({ query, accountId, includeGoals }) {
      if (includeGoals === false) {
        return new Promise((resolve) => {
          resolve({ query });
        });
      }
      const mentionQuery = ({ query, accountId, includeGoals }) => {
        const q = [];
        if (includeGoals) {
          q.push(...goalsByTitle(query, accountId, 'goals'));
        }
        return q;
      };
      return dogma.query(
        mentionQuery({ query, accountId, includeGoals }),
      ).then((response) => {
        if (response.status !== 200) {
          throw new Error('failed to query mentions');
        }
        return { response, query };
      });
    },
    buildRecentlyVisitedItems() {
      const targets = ['goal'];
      this.getEnrichedPageVisits().then((pageVisits) => {
        this.recentlyVisitedItems = pageVisits.reduce((res, next) => {
          const target = targets.find((t) => next.node && next.node.type === t);
          if (target === undefined || isNullOrUndefined(next.node.title)) {
            return res;
          }
          if (findInArray({ haystack: res, needle: next.node.uid }) !== null) {
            return res;
          }

          res.push({
            title: next.node.title,
            icon: next.node.icon,
            uid: next.node.uid,
            type: next.node.type,
          });
          return res;
        }, []);
      });
    },
    handleMouseover(index) {
      if (this.navigatingWith !== 'mouse') {
        return;
      }
      this.itemIndex = index;
    },
    blur() {
      this.disabled = true;
      this.reset();
    },
    reset() {
      this.query = null;
      this.filteredItems = [];
      this.suggestionRange = null;
      this.itemIndex = 0;
    },
    newMenu() {
      return new ContextMenuExtension({
        matcher: {
          char: '@',
          allowSpaces: true,
          startOfLine: false,
        },
        appendText: ' ',
        suggestionClass: 'mention-menu',
        command: this.command,
        items: this.items,
        element: this.$el,
        onEnter: ({ items, query, range, virtualNode, view }) => {
          if (view.editable === false) {
            return;
          }
          this.disabled = false;
          this.schema = markRaw(view.state.schema);
          this.view = markRaw(view);
          this.query = query.trim();
          this.filteredItems = items;
          this.suggestionRange = range;
          this.renderPopup(virtualNode);
        },
        onChange: ({ items, query, range, virtualNode }) => {
          this.query = query.trim();
          this.filteredItems = items;
          this.suggestionRange = range;
          this.itemIndex = 0;
          this.renderPopup(virtualNode);
        },
        onExit: () => {
          this.reset();
        },
        onKeyDown: ({ event }) => {
          if (event.key === 'ArrowUp') {
            this.upHandler();
            return true;
          }
          if (event.key === 'ArrowDown') {
            this.downHandler();
            return true;
          }
          if (event.key === 'Enter') {
            this.enterHandler();
            return true;
          }
          return false;
        },
        onFilter: (items, query) => {
          const debounceKey = 'retrieveData';
          this.query = query.trim();
          if (isEmpty(this.query)) {
            this.debounceCancel(debounceKey);
            this.adjustPosition();
            return this.items;
          }

          const retrieveData = () => {
            this.loading = true;
            this.doGetMentions({
              query: this.query,
              accountId: this.account.uid,
              includeGoals: this.allowedContent.includes(editorNodeType.goalMention),
            }).then(({ response, query }) => {
              if (this.query !== query) {
                return;
              }

              if (this.allowedContent.includes(editorNodeType.mention)) {
                this.pushFilteredItems(
                  this.searchUsers(query).map((u) => ({
                    ...u,
                    title: displayName(u),
                    type: 'user',
                  })),
                );
              }
              if (this.allowedContent.includes(editorNodeType.goalMention)) {
                this.pushFilteredItems(
                  response.data.goals.map((g) => ({
                    ...g,
                    type: 'goal',
                  })),
                );
              }
              this.adjustPosition();
            }).catch(() => {
              this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
            }).finally(() => {
              this.loading = false;
            });
          };

          this.debounce(retrieveData, 300, debounceKey);

          this.adjustPosition();
          const matches = this.items.filter((i) => i.title.toLowerCase().includes(this.query.toLowerCase()));
          if (!this.resourcesAllowed) {
            return matches;
          }
          const resources = this.matchingResources(this.query)
            .filter((r) => !matches.some((m) => m.uid === r.uid && m.type === r.type));
          return [...matches, ...resources];
        },
      });
    },
    upHandler() {
      if (this.itemIndex - 1 <= 0) {
        this.itemIndex = 0;
        return;
      }
      this.navigatingWith = 'keyboard';
      const oldVal = this.itemIndex;
      this.itemIndex -= 1;
      this.setScrollPosition(this.itemIndex, oldVal);
    },
    downHandler() {
      if (this.itemIndex + 1 >= this.orderedItems.length) {
        return;
      }
      this.navigatingWith = 'keyboard';
      const oldVal = this.itemIndex;
      this.itemIndex += 1;
      this.setScrollPosition(this.itemIndex, oldVal);
    },
    enterHandler() {
      const item = this.orderedItems[this.itemIndex];
      if (typeof item === 'undefined') {
        return;
      }

      this.addBlock(item, this.suggestionRange.from, this.suggestionRange.to);
      this.query = null;
    },
    handleClick(item, event) {
      event.stopPropagation();
      event.preventDefault();
      this.addBlock(item, this.suggestionRange.from, this.suggestionRange.to);
    },
    addBlock(item, from, to) {
      switch (item.type) {
        case 'user':
          this.addUser(item, from, to);
          break;
        case 'goal':
          this.addGoal(item, from, to);
          break;
        case spaceConfig.model:
        case gridPageConfig.model:
          this.addResource(item, from, to);
          break;
        default:
          return;
      }

      this.editor.focus();
    },
    addUser(item, from, to) {
      const node = this.editor.schema.nodes.mention.create({ id: item.uid, label: item.title });
      const transaction = this.editor.view.state.tr.replaceRangeWith(from, to, node);
      this.editor.view.dispatch(transaction);
      insertText(' ')(this.view.state, this.view.dispatch);
    },
    addGoal(item, from, to) {
      const node = this.editor.schema.nodes.goalMention.create({ id: item.uid, icon: item.icon, title: item.title });
      const transaction = this.editor.view.state.tr.replaceRangeWith(from, to, node);
      this.editor.view.dispatch(transaction);
      insertText(' ')(this.editor.view.state, this.editor.view.dispatch);
    },
    addResource(item, from, to) {
      const node = this.editor.schema.nodes.resourceMention.create({ kind: item.type, id: item.uid, title: item.title });
      const transaction = this.editor.view.state.tr.replaceRangeWith(from, to, node);
      this.editor.view.dispatch(transaction);
      insertText(' ')(this.editor.view.state, this.editor.view.dispatch);
    },
    renderPopup(node) {
      if (this.popup) {
        return;
      }

      const dimensions = node.getBoundingClientRect();
      this.x = dimensions.x;
      this.y = dimensions.y;
    },
    setScrollPosition(val, oldVal) {
      if (typeof this.$refs[`item_${val}`] === 'undefined' || typeof this.$refs[`item_${val}`][0] === 'undefined' || typeof this.$refs.card === 'undefined') {
        return;
      }

      const currentItem = this.$refs[`item_${val}`][0].$el;
      const card = this.$refs.card.$el;

      if (oldVal < val && currentItem.clientHeight + currentItem.offsetTop > card.clientHeight) {
        card.scrollTop = currentItem.clientHeight * (val + 1) - card.clientHeight + this.headingHeights(val) + 12;
        return;
      }
      if (currentItem.offsetTop < card.scrollTop) {
        card.scrollTop = currentItem.clientHeight * val;
      }
    },
    headingHeights(val) {
      return 31 * (this.sections.findIndex((section) => val < section.offset + section.items.length) + 1);
    },
    adjustPosition() {
      this.$nextTick(() => {
        if (typeof this.$refs.card !== 'undefined' && this.y + this.$refs.card.$el.clientHeight > document.body.clientHeight) {
          this.bottom = document.body.clientHeight - this.y + 5;
        }
      });
    },
    pushFilteredItems(items) {
      items.forEach((item) => {
        const index = this.filteredItems.findIndex((i) => i.uid === item.uid && i.type === item.type);
        if (index === -1) {
          this.filteredItems.push(item);
          return;
        }

        this.filteredItems[index] = item;
      });
    },
  },
  watch: {
    orderedItems() {
      if (this.showSuggestions) {
        this.adjustPosition();
      }
    },
    showSuggestions(show) {
      if (show) {
        if (this.recentlyVisitedItems.length === 0) {
          this.buildRecentlyVisitedItems();
        }
        this.call += 1;
        this.adjustPosition();
        return;
      }

      this.bottom = 0;
    },
  },
  mixins: [ComponentList],
};
</script>

<style scoped lang="scss" type="text/scss">
  .m-context-menu {
    z-index: 1;

    ._sub-heading {
      height: 3.1rem;
      font-size: $font-size-2;
      color: $font-color-tertiary;
    }

    ._empty {
      color: $font-color-secondary;
    }
  }

  ._overlay {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 99;
    width: 100vw;
    height: var(--viewport-height-100);
  }
</style>
